import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/api";
import { HomeContent, RatePackage } from "@/types";
import { OSChrome } from "@/components/os/OSChrome";

const KIND_LABEL: Record<string, string> = {
  subscription: "MONTHLY SUB",
  individual: "PER VIDEO",
  custom: "SCOPED",
};

const PackageCard = ({
  p, i, tint, onInquire,
}: { p: RatePackage; i: number; tint: string; onInquire: () => void }) => (
  <motion.div
    data-testid={`package-${p.id}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ delay: i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="border-[3px] border-ink bg-white shadow-hard"
  >
    <div className="flex items-center justify-between gap-2 border-b-[3px] border-ink bg-lnpink px-3 py-2 font-spacemono text-[10px] font-bold uppercase tracking-wider">
      <span className="truncate">{p.name}.pkg</span>
      <span className="shrink-0 border-2 border-ink bg-white px-1.5 py-0.5">{KIND_LABEL[p.kind] || p.kind}</span>
    </div>
    <div className={`${tint} p-5`}>
      <p data-testid={`package-price-${p.id}`} className="font-pixel text-xl leading-relaxed sm:text-2xl">
        {p.price_label}
      </p>
      <p className="mt-2 font-spacemono text-[10px] font-bold tracking-widest text-ink/60">{p.name}</p>
      <ul className="mt-4 flex flex-col gap-2.5">
        {p.inclusions.map((inc, j) => (
          <li key={j} className="flex items-start gap-2.5 text-sm font-medium leading-snug">
            <span className="mt-1.5 h-2 w-2 shrink-0 bg-ink" aria-hidden />
            {inc}
          </li>
        ))}
      </ul>
      {p.kind === "custom" && (
        <button data-testid="custom-inquire-btn" onClick={onInquire} className="btn-chunky mt-5 bg-lnpink px-4 py-2.5 text-xs">
          OPEN INQUIRY <ArrowRight size={13} />
        </button>
      )}
    </div>
  </motion.div>
);

const SectionLabel = ({ children }: { children: string }) => (
  <div className="mb-4 mt-9 flex items-center gap-3 first:mt-0">
    <span className="border-2 border-ink bg-ink px-2 py-1 font-spacemono text-[10px] font-bold tracking-widest text-butter">
      {children}
    </span>
    <span className="h-0.5 flex-1 bg-ink/20" aria-hidden />
  </div>
);

export default function RatesPage() {
  const nav = useNavigate();
  const [packages, setPackages] = useState<RatePackage[]>([]);
  const [content, setContent] = useState<HomeContent | null>(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const subs = packages.filter((p) => p.kind === "subscription");
  const indiv = packages.filter((p) => p.kind === "individual");
  const custom = packages.filter((p) => p.kind === "custom");
  const goInquire = () => nav("/inquire");

  return (
    <OSChrome id="rates" title="rate_card.exe — Services & Subscriptions" tint="lavender">
      <div className="p-5 sm:p-8">
        <h2 data-testid="rates-heading" className="font-pixel text-lg leading-relaxed sm:text-2xl">
          SERVICES &amp; SUBSCRIPTIONS
        </h2>
        <p className="mt-3 max-w-lg text-sm font-medium text-ink/75">
          Flat rates. No surprise invoices at 2AM. Pick your plan, and we'll cut straight to the good stuff!
        </p>

        {subs.length > 0 && <SectionLabel>SUBSCRIPTIONS — PRIME TIME PLANS</SectionLabel>}
        <div className="grid gap-5 md:grid-cols-2">
          {subs.map((p, i) => <PackageCard key={p.id} p={p} i={i} tint="bg-lavender" onInquire={goInquire} />)}
        </div>

        {indiv.length > 0 && <SectionLabel>INDIVIDUAL — PAY-PER-VIEW</SectionLabel>}
        <div className="grid gap-5 md:grid-cols-2">
          {indiv.map((p, i) => <PackageCard key={p.id} p={p} i={i} tint="bg-mint" onInquire={goInquire} />)}
        </div>

        {custom.length > 0 && <SectionLabel>CUSTOM — OFF THE GUIDE</SectionLabel>}
        <div className="grid gap-5 md:grid-cols-2">
          {custom.map((p, i) => <PackageCard key={p.id} p={p} i={i} tint="bg-butter" onInquire={goInquire} />)}
        </div>

        {content?.rates_note && (
          <div data-testid="rates-note" className="mx-auto mt-10 max-w-md rotate-[1.5deg] border-2 border-ink bg-butter p-4 shadow-hard">
            <p className="font-spacemono text-xs font-bold leading-relaxed">{content.rates_note}</p>
          </div>
        )}

        <div className="mt-9 flex justify-center">
          <button data-testid="rates-cta" onClick={goInquire} className="btn-chunky bg-lnpink px-6 py-3 text-xs sm:text-sm">
            JOIN THE CREW <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </OSChrome>
  );
}
